import React from "react";
import classNames from "classnames";

import { Text } from "../../../atoms/Text/Text";
import { User } from "../../../models/User";
import {
  ChallengeModel,
  ChallengesAvailableOrSentModel,
  CreateChallengePayloadModel,
} from "../../../models/Challenge";
import { FriendTabs } from "../../../services/Friend/FriendService";
import ChallengeService, {
  ChallengeStates,
} from "../../../services/Friend/ChallengeService";
import ChallengeApiService from "../../../services/Friend/ChallengeApiService";

import styles from "./FriendChallenge.css";
import moment from "moment";
import { Game } from "../../../models/Game";

type RenderChallengesByDateProps = {
  tabId: FriendTabs;
  isOpened: boolean;
  data: ChallengesAvailableOrSentModel;
  user: User;
  game: Game;
  onTabSelect: (tabId: FriendTabs, gameId?: number) => void;
  onCreateNewChallenge: (payload: CreateChallengePayloadModel) => Promise<void>;
  onDismissNotification: (challengeId: number) => Promise<void>;
};

const LEARN_MORE_URL = "https://www.aarp.org/entertainment/games/info-2022/right-again-trivia-sports-game-how-to-play/"

export class RenderChallengesByDate extends React.Component<RenderChallengesByDateProps> {
  state = {
    selectedChallenge: undefined,
    selectedWager: undefined,
    activeAccordion: undefined,
    activeViewMore: false,
    wagerScore: 0,
    isSavingChallenge: false,
    dismissedNotifications: [],
  };

  componentDidMount() {
    this.loadDateWagerSumup();
  }

  formatDate(date) {
    return moment.utc(date).local().format("DD/MM/YY hh:mm A zz");
  }

  async loadDateWagerSumup() {
    return ChallengeApiService.getWagerSumup(
      this.props.data.gameInfo.puzzleId
    ).then((wagerScore) => {
      this.setState({ wagerScore });
    });
  }

  handleCheck(i) {
    const isChecked = i === this.state.selectedChallenge;
    this.setState({
      selectedChallenge: isChecked ? undefined : i,
      ...((isChecked || this.state.wagerScore !== i) && {
        selectedWager: undefined,
      }),
    });
  }

  handleWagger(i) {
    const { selectedWager } = this.state;
    this.setState({
      selectedWager: i !== selectedWager ? i : undefined,
      ...(i !== selectedWager && { selectedChallenge: i }),
    });
  }

  handleToggle = (challengeId, dismissNotification) => {
    this.setState({
      activeAccordion:
        this.state.activeAccordion === challengeId ? undefined : challengeId,
    });
    if (dismissNotification) {
      this.props.onDismissNotification(challengeId).then(() => {
        this.setState({
          dismissedNotifications: [
            ...this.state.dismissedNotifications,
            challengeId,
          ],
        });
      });
    }
  };

  handleViewMore = () => {
    this.setState({
      activeViewMore: !this.state.activeViewMore,
    });
  };

  handleCreateChallenge(friendCode, setWager) {
    const {
      data: { gameInfo },
      onCreateNewChallenge,
      game: { seo_ChallengePoints, slug },
    } = this.props;
    this.setState({ isSavingChallenge: true });
    onCreateNewChallenge({
      gameId: gameInfo.puzzleId,
      gameDate: new Date(gameInfo.date),
      friendCode,
      wagerScore: setWager ? parseInt(seo_ChallengePoints) : 0,
      puzzleKey: slug,
    }).then(() => {
      this.setState({ isSavingChallenge: false });
    });
  }

  renderChallengeResult(challenge: ChallengeModel) {
    const { challengeStatus, friendScore, userScore, friendName, wagerScore } =
      challenge;
    const isWinner = challengeStatus === ChallengeStates.win;
    const isDraw = challengeStatus === ChallengeStates.draw;

    return (
      <table className={styles.challenges_results_table}>
        <thead>
          <tr>
            <th>Player</th>
            <th>Score</th>
            <th>Wager</th>
            <th>
              <span className={styles.hidden_mobile}>Final Game Pts.</span>
              <span className={styles.hidden_desktop}>Final Pts.</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className={classNames({ [styles.winner_row]: isWinner })}>
            <td>{this.props.user.name}</td>
            <td>{challenge.userScore}</td>
            <td>
              <span>{`${isWinner ? "+" : "-"}${
                isDraw ? "-" : challenge.wagerScore
              }`}</span>
            </td>
            <td>
              {ChallengeService.calculateFinalPoints(
                challengeStatus,
                userScore,
                wagerScore
              ) || "--"}
            </td>
          </tr>

          <tr
            className={classNames({
              [styles.winner_row]: !isWinner && !isDraw,
            })}
          >
            <td>{friendName}</td>
            <td>{friendScore}</td>
            <td>
              <span>{`${isDraw ? "-" : isWinner ? "-" : "+"}${
                isDraw ? "-" : challenge.wagerScore
              }`}</span>
            </td>
            <td>
              {ChallengeService.calculateFinalPoints(
                isDraw
                  ? ChallengeStates.draw
                  : isWinner
                  ? ChallengeStates.lose
                  : ChallengeStates.win,
                friendScore,
                wagerScore
              )}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  renderExistingChallenges() {
    const {
      data: { challengesSent, gameInfo },
    } = this.props;
    const { activeAccordion, dismissedNotifications } = this.state;

    return (
      challengesSent.length > 0 && (
        <>
          <Text size="xs" className={styles.challenge_section_header}>
            Challenges
          </Text>
          <Accordion>
            {challengesSent.map((challenge) => (
              <AccordionItem
                active={activeAccordion === challenge.challengeId}
                key={challenge.challengeId}
              >
                <AccordionHeader
                  className={
                    challenge.hasFriendActivity &&
                    !dismissedNotifications.includes(challenge.challengeId) &&
                    styles.show_notification_badge
                  }
                  onClick={() =>
                    this.handleToggle(
                      challenge.challengeId,
                      challenge.hasFriendActivity
                    )
                  }
                >
                  <span>{challenge.friendName}</span>
                  <span>
                    <img width="10" src="/icons/check-icon.png" />
                  </span>
                  <span>{challenge.wagerScore}</span>
                  <span
                    className={classNames(styles.challenge_section_header, {
                      [styles.winner_status]:
                        challenge.challengeStatus === ChallengeStates.win,
                    })}
                  >
                    {ChallengeService.getChallengeStatusMessage(
                      challenge.challengeStatus
                    )}
                  </span>
                </AccordionHeader>
                <AccordionBody>
                  {["win", "lose", "draw"].includes(
                    challenge.challengeStatus
                  ) ? (
                    this.renderChallengeResult(challenge)
                  ) : (
                    <>
                      <Text className={styles.challenge_info}>
                        <b>Expires:</b>{" "}
                        {this.formatDate(
                          ChallengeService.getExpirationDate(
                            challenge.challengeDate,
                            challenge.expirationDate,
                            gameInfo.date
                          )
                        )}
                      </Text>
                      <Text className={styles.challenge_info}>
                        <b>Sent:</b> {this.formatDate(challenge.challengeDate)}
                      </Text>
                    </>
                  )}
                </AccordionBody>
              </AccordionItem>
            ))}
          </Accordion>
        </>
      )
    );
  }

  renderAvailableChallenges() {
    const {
      data: { friendsAvailableToChallenge },
      game: { seo_ChallengePoints },
    } = this.props;
    const { selectedChallenge, selectedWager, isSavingChallenge } = this.state;

    return (
      friendsAvailableToChallenge.length > 0 && (
        <table
          className={classNames([
            styles.challenges_table,
            styles.challenge_detail_table,
          ])}
        >
          <thead>
            <tr>
              <th>Available</th>
              <th>Challenge</th>
              <th>{seo_ChallengePoints} pt Wager</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {friendsAvailableToChallenge.map(({ friendName, friendCode }) => (
              <tr key={friendCode}>
                <td>
                  <UserName>{friendName}</UserName>
                </td>
                <td>
                  <Checkbox
                    checked={selectedChallenge === friendCode}
                    onClick={() => this.handleCheck(friendCode)}
                  />
                </td>
                <td>
                  <Checkbox
                    checked={selectedWager === friendCode}
                    onClick={() => this.handleWagger(friendCode)}
                  />
                </td>
                <td>
                  <button
                    disabled={selectedChallenge !== friendCode}
                    type="button"
                    onClick={() =>
                      this.handleCreateChallenge(friendCode, !!selectedWager)
                    }
                  >
                    {selectedChallenge === friendCode && isSavingChallenge
                      ? "Saving..."
                      : "Challenge"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )
    );
  }

  render() {
    const {
      onTabSelect,
      data: { challengesSent, friendsAvailableToChallenge, gameInfo },
      isOpened,
      tabId,
      game: { seo_ChallengePoints },
    } = this.props;
    const { wagerScore, activeViewMore } = this.state;

    return (
      <div
        role="tabpanel"
        id={tabId}
        className={classNames({ [styles.hiddenFriendTable]: !isOpened })}
        aria-labelledby={`tab${tabId}`}
      >
        <BackButton onClick={() => onTabSelect(FriendTabs.ViewSendChallenges)}>
          Challenge
        </BackButton>
        <Text
          className={classNames(styles.challenge_cta, {
            [styles.open]: activeViewMore,
          })}
        >
          Challenge your friends, who haven't yet played the specific game date,
          to see who achieves the high score! You can challenge any of your
          friends without wagers. Bragging rights are fun! But, you can also
          place a {seo_ChallengePoints} game point wager on up to three
          challenges. There is a cap of three wagers per game date, and you can
          challenge a friend only once per game date.{" "}
          <span className={styles.underline}>
            Only your first play of the game on a specific date counts against a
            challenge, so make it count!
          </span>
          {" "}
          <a className={styles.anchor} target="_blank" href={LEARN_MORE_URL}>
            Learn more
          </a>
        </Text>
        <span
          className={classNames(styles.viewMore, {
            [styles.open]: activeViewMore,
          })}
          onClick={() => this.handleViewMore()}
        >
          View {activeViewMore ? "Less" : "More"}
        </span>

        <div className={styles.challenge_header}>
          {gameInfo && (
            <div>
              <h2 className={styles.game_date}>
                {ChallengeService.formatDateToLiteral(gameInfo.date)}
              </h2>
              <a
                href={
                  gameInfo.availableToPlay &&
                  `?toGo=game_${ChallengeService.formatDate(gameInfo.date)}`
                }
                className={classNames(styles.button, {
                  [styles.disabled]: !gameInfo.availableToPlay,
                })}
              >
                Play
              </a>
            </div>
          )}

          <ScoreCounter score={wagerScore} />
        </div>

        {friendsAvailableToChallenge && this.renderAvailableChallenges()}

        {challengesSent && this.renderExistingChallenges()}
      </div>
    );
  }
}

const BackButton = (props: any) => (
  <span className={styles.back_btn} {...props} />
);
const UserName = (props: { you?: boolean; [key: string]: any }) => (
  <Text
    className={classNames(styles.userName, { [styles.userNameYou]: props.you })}
    {...props}
  />
);

const Checkbox = ({ checked, ...props }: any) => (
  <div
    className={classNames(styles.checkbox, { [styles.checked]: checked })}
    {...props}
  />
);

const Accordion = (props: any) => (
  <ul
    className={classNames(styles.accordion, {
      [styles.inverted]: props.inverted,
    })}
    {...props}
  />
);
const AccordionItem = (props: any) => (
  <li
    className={classNames(styles.accordion_item, {
      [styles.active]: props.active,
    })}
    {...props}
  />
);
const AccordionHeader = ({ children, className, ...props }: any) => (
  <div className={classNames(styles.accordion_header, className)} {...props}>
    {children}
  </div>
);
const AccordionBody = (props: any) => (
  <div className={styles.accordion_body_wrapper}>
    <div className={styles.accordion_body}>{props.children}</div>
  </div>
);

const ScoreCounter = ({ score }: { score?: number }) => (
  <div className={styles.score_counter}>
    <Text className={styles.points}>{score}</Text>
    <Text>Wager pts. Won</Text>
  </div>
);

export default Accordion;
